import {
  MutationOptions,
  queryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';

import { crownApi, crownQueryClient } from '@/common/api/crownQueryClient';
import {
  SOCURE_REASON_CODES,
  SocureFinding,
} from '@/components/ui/review/oscilar/SocureReasonCodes';
import { debugDeviceData } from '@/features/administrator-panel/identity-verification/oscilar/device-data.debug';
import { debugWatchlistData } from '@/features/administrator-panel/identity-verification/oscilar/watchlist-data.debug';
import { Components, IndividualKYCSource } from '@/openapi';
import { cloneDeep } from 'lodash';
import { useMemo } from 'react';

export const useIDVVendor = ({ userId }: { userId: string }) => {
  const { data: identityVerification } = useSuspenseQuery(
    getIdentityVerificationQuery(userId),
  );

  const idvVendor = useMemo(() => {
    if (identityVerification?.individualKYC) {
      return identityVerification?.individualKYC.source;
    }
    return null;
  }, [identityVerification?.individualKYC]);

  return idvVendor ?? null;
};

// Displays all Socure data and reason codes
const DEBUG_MODE = false as boolean;
const debugEnhance = (
  individualKYC: Components.Schemas.IdentityVerificationReviewGetVerificationResponseDto['individualKYC'],
) => {
  if (!individualKYC) return individualKYC;
  if (individualKYC.source !== 'OSCILAR') return individualKYC;
  const populatedKYC = cloneDeep(individualKYC)!;
  const { identityData } = populatedKYC || {};
  if (identityData?.source === 'OSCILAR') {
    let socureResults = identityData?.outputs?.variables?.socureResults;
    if (socureResults) {
      socureResults = cloneDeep(socureResults);
      Object.keys(socureResults).forEach((key) => {
        const codeGroup = (
          SOCURE_REASON_CODES as Record<string, Record<string, SocureFinding>>
        )[key];
        if (codeGroup) {
          // @ts-expect-error wonky inference
          socureResults[key].reasonCodes = Object.keys(codeGroup);
        }
      });
      socureResults = {
        ...socureResults,
        ...debugDeviceData,
      };
      if (socureResults.fraud?.scores?.[0]?.score) {
        socureResults.fraud.scores![0].score = 0.36;
      }
      if (socureResults.namePhoneCorrelation?.score) {
        socureResults.namePhoneCorrelation.score = 0.84;
      }
      if (socureResults.nameAddressCorrelation?.score) {
        socureResults.nameAddressCorrelation.score = 1.0;
      }

      if ('outputs' in populatedKYC.identityData!) {
        populatedKYC!.identityData!.outputs!.variables!.socureResults =
          socureResults;
      }
    }
  }
  const { watchlistData } = populatedKYC || {};
  if (watchlistData?.source === 'OSCILAR') {
    let socureResults = watchlistData?.outputs?.variables?.socureResults;
    if (socureResults) {
      socureResults = cloneDeep(socureResults);
      Object.keys(socureResults).forEach((key) => {
        const codeGroup = (
          SOCURE_REASON_CODES as Record<string, Record<string, SocureFinding>>
        )[key];
        if (codeGroup) {
          // @ts-expect-error wonky inference
          socureResults[key].reasonCodes = Object.keys(codeGroup);
        }
      });
      if (socureResults.alertList) {
        socureResults.alertList.matches = debugWatchlistData.alertList.matches;
      }
      if (socureResults.globalWatchlist) {
        socureResults.globalWatchlist.matches =
          debugWatchlistData.globalWatchlist.matches;
      }

      if ('outputs' in populatedKYC.watchlistData!) {
        populatedKYC!.watchlistData!.outputs!.variables!.socureResults =
          socureResults;
      }
    }
  }

  return populatedKYC;
};

// Enforces proper discriminated type
export function useIndividualKYC<T extends IndividualKYCSource>({
  userId,
  source,
}: {
  userId: string;
  source: T;
}) {
  const individualKYCQuery = useSuspenseQuery(
    getIdentityVerificationQuery(userId),
  );

  let individualKYC = useMemo(() => {
    if (individualKYCQuery.data?.individualKYC?.source !== source) {
      return null;
    }

    return individualKYCQuery.data?.individualKYC ?? null;
  }, [individualKYCQuery.data]);

  if (DEBUG_MODE) {
    individualKYC = debugEnhance(individualKYC);
  }

  const discriminatedKYC = useMemo(() => {
    if (!individualKYC) return null;
    if (individualKYC.source !== source) return null;

    const identityData =
      individualKYC.identityData?.source === source
        ? (individualKYC.identityData as typeof individualKYC.identityData & {
            source: T;
          })
        : null;
    const watchlistData =
      individualKYC.watchlistData?.source === source
        ? (individualKYC.watchlistData as typeof individualKYC.watchlistData & {
            source: T;
          })
        : null;

    return {
      ...(individualKYC as typeof individualKYC & { source: T }),
      identityData,
      watchlistData,
    };
  }, [individualKYC]);

  return discriminatedKYC;
}

export const getIdentityVerificationQuery = (userId: string) =>
  queryOptions({
    queryFn: async () =>
      (
        await crownApi.IdentityVerificationReview_getUserIdentityVerification({
          userId,
        })
      ).data,
    queryKey: ['identity-verification', userId, 'identity-verification'],
  });

export const getIDVCapabilitiesQuery = () =>
  queryOptions({
    queryFn: async () =>
      (await crownApi.IdentityVerificationReview_getIDVCapabilities()).data,
    queryKey: ['identity-verification', 'capabilities'],
  });

export const resetIdentityVerificationMutation: MutationOptions<
  Components.Schemas.IdentityVerificationReviewResetVerificationResponseDto,
  never,
  {
    userId: string;
  }
> = {
  mutationFn: async ({ userId }) =>
    (
      await crownApi.IdentityVerificationReview_resetIdentityVerification({
        userId: userId,
      })
    ).data,
  onSuccess: async (data, { userId }) => {
    crownQueryClient.setQueryData(
      getIdentityVerificationQuery(userId).queryKey,
      data,
    );
  },
};

export const clearWatchlistScreeningMutation: MutationOptions<
  Components.Schemas.IdentityVerificationReviewGetIndividualWatchlistDto,
  never,
  {
    userId: string;
  }
> = {
  mutationFn: async ({ userId }) =>
    (
      await crownApi.IdentityVerificationReview_clearWatchlistScreening({
        userId: userId,
      })
    ).data,
  onSuccess: async (data, { userId }) => {
    crownQueryClient.setQueryData(
      ['identity-verification', userId, 'watchlist-screening'],
      data,
    );
    await crownQueryClient.invalidateQueries({
      queryKey: ['identity-verification', userId, 'identity-verification'],
    });
  },
};
